import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { Box } from '@mui/material';
import { onAutocompleteChangeInForm } from 'app/shared/components/form/Autocomplete';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { compose } from 'redux';

import { FieldType } from 'constants/formGenerator';
import { PendingChangeKind } from 'constants/pendingChanges';
import { transformNameInModelIntoFormName } from 'utils/formGenerator';
import { logger } from 'utils/logger';
import { getPendingChangeKind } from 'utils/pendingChanges';

import { getUuid } from 'store/auth/selectors';
import {
  selectFormHiddenFields,
  selectFormHiddenFieldsMap,
  selectFormLockedFields,
  selectFormLockedPage,
} from 'store/loanApplications/selectors';
import { selectAllFieldLocks, selectCurrentConnectionId } from 'store/presence/selectors';

import AddressAutocompleteCDS from '../../coreDesignSystem/form/AddressAutocompleteCDS';
import CheckboxCDS from '../../coreDesignSystem/form/CheckboxCDS';
import FormGeneratorAutocompleteCDS from '../../coreDesignSystem/formGenerator/FormGeneratorAutocompleteCDS';
import FormGeneratorCombinedFieldCDS from '../../coreDesignSystem/formGenerator/FormGeneratorCombinedFieldCDS';
import FormGeneratorDateFieldCDS from '../../coreDesignSystem/formGenerator/FormGeneratorDateFieldCDS';
import FormGeneratorSwitchCDS from '../../coreDesignSystem/formGenerator/FormGeneratorSwitchCDS';
import FormGeneratorTextFieldCDS from '../../coreDesignSystem/formGenerator/FormGeneratorTextFieldCDS';
import FieldPresenceWrapper from '../FieldPresenceWrapper';

import {
  getCombinedFieldEntries,
  getDefaultValue,
  getMinDateFieldProp,
} from './FormGenerator.utils';
import FormGeneratorCheckboxGroup from './FormGeneratorCheckboxGroup';
import FormGeneratorExtendableRadioGroup from './FormGeneratorExtendableRadioGroup';
import FormGeneratorGroup from './FormGeneratorGroup';
import FormGeneratorRadioGroup from './FormGeneratorRadioGroup/FormGeneratorRadioGroup';
import FormGeneratorRichTextEditor from './FormGeneratorRichTextEditor';

const defaultObject = {};

const getGroupGapStyles = groupGap => ({
  display: 'flex',
  flexDirection: 'column',
  gap: `${groupGap}px`,
});

const inputPropsNotForUI = [
  'widthInColumns',
  'withStaticHelper',
  'wrapperStyles',
  'maxDateValidationMessage',
  'minDateValidationMessage',
  'includeLocalOffset',
  'isMinDateBeforeDefault',
  'patternRegExp',
  'validationMessage',
  'isOutsideUsa',
  'staticOptions',
  'customOnChange',
  'customOnBlur',
  'customOnFocus',
  'customSchema',
  'presenceGroupKey',
  'key',
];

const defaultMethods = {
  onGroupClick: undefined,
  onGroupEdit: undefined,
  onGroupDelete: undefined,
};

const FormGenerator = ({ children, ...formGeneratorProps }) => {
  const {
    config,
    isLoading,
    isDisabled = false,
    form,
    handleChange,
    validationErrors,
    setInForm,
    validateField,
    methods = defaultMethods,
    isInModal = false,
    formName,
    staticValidateField,
    isNestedForm,
    numberOfColumns = 4,
    changedFields = defaultObject,
    renderControls,
    disabledPendingChangesHighlight = false,
    pendingChanges = defaultObject,
    ignoreEmptyGroups,
    groupGap,
  } = formGeneratorProps;

  const autoLockDisabledFields = useSelector(selectFormLockedFields(formName));
  const autoLockHiddenFields = useSelector(selectFormHiddenFields(formName));
  const autoLockHiddenFieldsMap = useSelector(selectFormHiddenFieldsMap(formName));
  const isPageLocked = useSelector(selectFormLockedPage(formName));
  const fieldLocks = useSelector(selectAllFieldLocks);
  const currentUserId = useSelector(getUuid);
  const currentConnectionId = useSelector(selectCurrentConnectionId);

  const nestedFormGeneratorProps = useMemo(
    () => ({
      isLoading,
      isDisabled,
      form,
      handleChange,
      validationErrors,
      setInForm,
      validateField,
      changedFields,
      isNestedForm,
      numberOfColumns,
      pendingChanges,
      disabledPendingChangesHighlight,
      formName,
    }),
    [
      isLoading,
      form,
      handleChange,
      validationErrors,
      setInForm,
      isDisabled,
      validateField,
      changedFields,
      isNestedForm,
      pendingChanges,
      numberOfColumns,
      disabledPendingChangesHighlight,
      formName,
    ],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const onAutocompleteChange = useCallback(
    onAutocompleteChangeInForm({ setInForm, validateField }),
    [setInForm, validateField],
  );

  const getFormTypeDependingFields = useCallback(
    ({ form, validationErrors, nameInModel, type, inputProps }) => {
      if (isNestedForm) {
        const defaultValue = getDefaultValue({ type, inputProps });
        const value = _.get(form, nameInModel, defaultValue);
        const helperText = _.get(validationErrors, nameInModel, '');

        return {
          value,
          helperText,
          name: nameInModel,
          error: !!helperText,
        };
      }

      const fieldNameInForm = transformNameInModelIntoFormName(nameInModel);

      return {
        error: !!validationErrors[fieldNameInForm],
        helperText: validationErrors[fieldNameInForm],
        name: fieldNameInForm,
        value: form[fieldNameInForm],
      };
    },
    [isNestedForm],
  );

  useEffect(() => {
    if (!config.groups) {
      logger.warn('Trying to render FormGenerator without required params (groups)', config);
    }
  }, [config]);

  const lockedGroupLocks = useMemo(() => {
    const map = new Map();

    config.groups?.forEach(group => {
      if (!group.key || !group.fields || !group.lockAsGroup) {
        return;
      }

      const lockedField = group.fields.find(field => {
        if (field.type === FieldType.GROUP) {
          return false;
        }

        const lockPath = field.nameInModel?.toLowerCase();
        const lock = lockPath ? fieldLocks[lockPath] : null;

        return !!lock && lock.connectionId !== currentConnectionId;
      });

      if (lockedField) {
        const lockPath = lockedField.nameInModel?.toLowerCase();
        map.set(group.key, fieldLocks[lockPath]);
      }
    });

    return map;
  }, [config.groups, fieldLocks, currentConnectionId]);

  const renderFormItem = useCallback(
    (
      {
        type,
        // Field Props
        inputProps = {},
        nameInModel,
        combinedFields,
        fieldId,
        noValidate,
        additionalContentBySelectedOption,
        dependentFields,
        lockGroup,
        // Group Props
        title,
        groupTitle,
        fields,
        key,
        lockAsGroup,
        methods: customMethods,
        additionalGroupContentConfig,
        dataTestId,
        customStyles,
        customControls,
      },
      index,
    ) => {
      const formTypeDependingFields = getFormTypeDependingFields({
        form,
        validationErrors,
        nameInModel,
        type,
        inputProps,
      });

      const resolveCombinedFieldValues = entry =>
        getFormTypeDependingFields({ form, validationErrors, ...entry });

      const columns = inputProps.columns || 1;
      const { direction } = inputProps;
      const fieldProps = _.omit(inputProps, inputPropsNotForUI);
      const minDate = getMinDateFieldProp(inputProps);
      const fieldPendingChange = _.get(pendingChanges, nameInModel);
      const fieldChangeKind = disabledPendingChangesHighlight
        ? null
        : getPendingChangeKind(fieldPendingChange);
      const isFieldChanged = fieldChangeKind === PendingChangeKind.DIRECT;
      const selectedChangedFields = _.get(changedFields, nameInModel);
      const customFieldStyles = inputProps.customStyles;

      const isHiddenByAutoLock = !!autoLockHiddenFields.find(field => field === fieldId);

      if (isHiddenByAutoLock) {
        return null;
      }

      const isDisabledByAutoLock = !!autoLockDisabledFields.find(field => field === fieldId);

      const effectiveLockPath = nameInModel?.toLowerCase();
      const fieldLock = effectiveLockPath ? fieldLocks[effectiveLockPath] : null;
      const isFieldDirectlyLocked = !!fieldLock && fieldLock.userId !== currentUserId;
      const groupLock = lockGroup ? lockedGroupLocks.get(lockGroup) : null;
      const isLockedByAnotherUser = isFieldDirectlyLocked || !!groupLock;

      const commonProps = {
        ...fieldProps,
        ...formTypeDependingFields,
        isFieldChanged,
        columns,
        direction,
        disabled:
          inputProps.disabled ||
          isLoading ||
          isDisabled ||
          isPageLocked ||
          isDisabledByAutoLock ||
          isLockedByAnotherUser,
        onChange: handleChange,
        noValidate,
        customStyles: customFieldStyles,
        inputProps: {
          maxLength: inputProps.maxLength,
        },
      };

      const fieldKey = inputProps.fieldKey ?? nameInModel;

      const isCheckboxField = type === FieldType.CHECKBOX;
      const wrapperStyles = isCheckboxField
        ? { alignSelf: 'center', marginTop: '24px', ...inputProps.wrapperStyles }
        : inputProps.wrapperStyles;

      const fieldPresenceProps = {
        columns,
        fieldId,
        formName,
        fieldPath: effectiveLockPath,
        formValue: formTypeDependingFields.value,
        groupLock,
        wrapperStyles,
        pendingChangeAuthors: isFieldChanged ? (fieldPendingChange?.authors ?? null) : null,
        fieldLabel: _.isString(inputProps.label) ? inputProps.label : '',
      };

      switch (type) {
        case FieldType.SELECT:
          return (
            <FieldPresenceWrapper key={fieldKey} {...fieldPresenceProps}>
              <FormGeneratorAutocompleteCDS
                {...commonProps}
                fieldChangeKind={fieldChangeKind}
                onChange={inputProps.customOnChange || onAutocompleteChange}
              />
            </FieldPresenceWrapper>
          );

        case FieldType.RICH_TEXTAREA:
          return (
            <FieldPresenceWrapper key={fieldKey} {...fieldPresenceProps}>
              <FormGeneratorRichTextEditor
                {...commonProps}
                onChange={inputProps.customOnChange || commonProps.onChange}
              />
            </FieldPresenceWrapper>
          );

        case FieldType.TEL:
        case FieldType.INPUT:
          return (
            <FieldPresenceWrapper key={fieldKey} {...fieldPresenceProps}>
              <FormGeneratorTextFieldCDS
                {...commonProps}
                fieldChangeKind={fieldChangeKind}
                onChange={inputProps.customOnChange || commonProps.onChange}
              />
            </FieldPresenceWrapper>
          );

        case FieldType.COMBINED:
          return (
            <FieldPresenceWrapper key={fieldKey} {...fieldPresenceProps}>
              <FormGeneratorCombinedFieldCDS
                {...commonProps}
                entries={getCombinedFieldEntries({
                  combinedFields,
                  omittedInputProps: inputPropsNotForUI,
                  resolveFieldValues: resolveCombinedFieldValues,
                  onChange: inputProps.customOnChange || commonProps.onChange,
                })}
              />
            </FieldPresenceWrapper>
          );

        case FieldType.CHECKBOX:
          return (
            <FieldPresenceWrapper key={fieldKey} {...fieldPresenceProps}>
              <CheckboxCDS
                {...commonProps}
                dataTestId={dataTestId}
                onChange={inputProps.customOnChange || commonProps.onChange}
              />
            </FieldPresenceWrapper>
          );

        case FieldType.CHECKBOX_GROUP:
          return (
            <FieldPresenceWrapper key={fieldKey} {...fieldPresenceProps}>
              <FormGeneratorCheckboxGroup
                setInForm={setInForm}
                {...commonProps}
                changedOptions={selectedChangedFields}
                onChange={inputProps.customOnChange || commonProps.onChange}
              />
            </FieldPresenceWrapper>
          );

        case FieldType.RADIO:
          return (
            <FieldPresenceWrapper key={fieldKey} {...fieldPresenceProps}>
              <FormGeneratorRadioGroup
                {...commonProps}
                onChange={inputProps.customOnChange || commonProps.onChange}
              />
            </FieldPresenceWrapper>
          );

        case FieldType.SWITCH:
          return (
            <FieldPresenceWrapper key={fieldKey} {...fieldPresenceProps}>
              <FormGeneratorSwitchCDS
                {...commonProps}
                value={!!commonProps.value}
                onChange={inputProps.customOnChange || commonProps.onChange}
              />
            </FieldPresenceWrapper>
          );

        case FieldType.EXTENDABLE_RADIO:
          return (
            <FieldPresenceWrapper key={fieldKey} {...fieldPresenceProps}>
              <FormGeneratorExtendableRadioGroup
                {...commonProps}
                additionalContentBySelectedOption={additionalContentBySelectedOption}
                formGeneratorProps={nestedFormGeneratorProps}
                onChange={inputProps.customOnChange || commonProps.onChange}
              />
            </FieldPresenceWrapper>
          );

        case FieldType.DATE:
          return (
            <FieldPresenceWrapper key={fieldKey} {...fieldPresenceProps}>
              <FormGeneratorDateFieldCDS
                {...commonProps}
                fieldChangeKind={fieldChangeKind}
                minDate={minDate}
                setInForm={setInForm}
                validateField={validateField}
                onBlur={inputProps.customOnBlur}
                onChange={inputProps.customOnChange || commonProps.onChange}
                onFocus={inputProps.customOnFocus}
              />
            </FieldPresenceWrapper>
          );

        case FieldType.ADDRESS:
          return (
            <FieldPresenceWrapper key={fieldKey} {...fieldPresenceProps}>
              <AddressAutocompleteCDS
                {...commonProps}
                dependentFields={dependentFields}
                isNestedForm={isNestedForm}
                setInForm={setInForm}
                staticValidateField={staticValidateField}
                onChange={inputProps.customOnChange || commonProps.onChange}
              />
            </FieldPresenceWrapper>
          );

        case FieldType.EMPTY:
          return <div key={fieldKey} />;

        case FieldType.GROUP: {
          const groupFields = fields.map(field => ({
            ...field,
            ...(lockAsGroup ? { lockGroup: key } : {}),
            inputProps: { ...field.inputProps, presenceGroupKey: key },
          }));

          const groupProps = {
            isLoading,
            numberOfColumns,
            fields: groupFields,
            title,
            groupTitle,
            index,
            isInModal,
            dataTestId,
            onGroupClick: methods.onGroupClick,
            onGroupDelete: methods.onGroupDelete,
            methods: customMethods,
            customStyles,
            additionalGroupContentConfig,
            renderFormItem,
            renderControls,
            customControls,
            ignoreEmptyGroups,
            hiddenFieldsMap: autoLockHiddenFieldsMap,
          };

          return <FormGeneratorGroup key={key} {...groupProps} />;
        }

        default:
          return null;
      }
    },
    [
      getFormTypeDependingFields,
      form,
      formName,
      validationErrors,
      disabledPendingChangesHighlight,
      pendingChanges,
      changedFields,
      autoLockDisabledFields,
      autoLockHiddenFields,
      autoLockHiddenFieldsMap,
      isPageLocked,
      fieldLocks,
      currentUserId,
      lockedGroupLocks,
      isLoading,
      isDisabled,
      handleChange,
      onAutocompleteChange,
      setInForm,
      nestedFormGeneratorProps,
      validateField,
      isNestedForm,
      staticValidateField,
      numberOfColumns,
      isInModal,
      methods.onGroupClick,
      methods.onGroupDelete,
      renderControls,
      ignoreEmptyGroups,
    ],
  );

  const renderedGroups = config.groups?.map?.(renderFormItem);

  if (groupGap) {
    return <Box sx={getGroupGapStyles(groupGap)}>{renderedGroups}</Box>;
  }

  return renderedGroups;
};

FormGenerator.propTypes = {
  ...WithLoadingPropTypes,
  config: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  validationErrors: PropTypes.object.isRequired,
  setInForm: PropTypes.func.isRequired,
  validateField: PropTypes.func.isRequired,
  methods: PropTypes.object,
  isInModal: PropTypes.bool,
  numberOfColumns: PropTypes.number,
  changedFields: PropTypes.object,
  pendingChanges: PropTypes.object,
  renderControls: PropTypes.func,
  customControls: PropTypes.node,
  formName: PropTypes.string,
  disabledPendingChangesHighlight: PropTypes.bool,
  isDisabled: PropTypes.bool,
  ignoreEmptyGroups: PropTypes.bool,
  groupGap: PropTypes.number,
};

export default compose(withLoading, memo)(FormGenerator);
